import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Loader2, CheckCircle2, Calendar } from "lucide-react";
import {
    BUSINESS_CONTEXT_URL,
    CONTACT_EMAIL,
    CONTACT_PHONE,
    CONTACT_PHONE_HREF,
} from "@/lib/brand";
import { useCalendlyUrl, openCalendly } from "@/lib/calendly";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
    name: "",
    email: "",
    business_name: "",
    phone: "",
    message: "",
    website: "", // honeypot — bots fill this; humans never see it
};

export default function ContactForm() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const calendlyUrl = useCalendlyUrl();

    const onChange = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const validate = () => {
        if (!form.name.trim()) return "Please enter your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            return "Please enter a valid email address.";
        if (!form.business_name.trim()) return "Please enter your business name.";
        if (!form.phone.trim() || form.phone.trim().length < 5)
            return "Please enter a valid phone number.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                business_name: form.business_name.trim(),
                phone: form.phone.trim(),
                message: form.message.trim(),
                website: form.website,
            });
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Message received. We'll be in touch shortly.");
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again or email us directly.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3.5 text-sm placeholder:text-[#666] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all duration-300 font-body";
    const labelClass =
        "text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] mb-2 block font-medium";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 luxe-radial-faint"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left: copy + contact */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="gold-rule-solid w-10" />
                            <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                                Contact Us
                            </span>
                        </div>

                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
                            Let&apos;s build the website your{" "}
                            <span className="italic text-gold-metallic">business deserves</span>.
                        </h2>

                        <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-[#C4C4C4] max-w-md">
                            Tell us a little about your business and we&apos;ll
                            arrange a personalized consultation tailored to your
                            goals.
                        </p>

                        <div className="mt-10 hairline-divider" />

                        <div className="mt-8 space-y-5">
                            <a
                                data-testid="contact-email-link"
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="group flex items-center gap-4 text-left"
                            >
                                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/[0.06] transition-all duration-300">
                                    <Mail
                                        className="w-4 h-4 text-[#D4AF37]"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#888]">
                                        Email
                                    </div>
                                    <div className="text-sm text-white group-hover:text-[#D4AF37] transition-colors">
                                        {CONTACT_EMAIL}
                                    </div>
                                </div>
                            </a>

                            <a
                                data-testid="contact-phone-link"
                                href={CONTACT_PHONE_HREF}
                                className="group flex items-center gap-4 text-left"
                            >
                                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/[0.06] transition-all duration-300">
                                    <Phone
                                        className="w-4 h-4 text-[#D4AF37]"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#888]">
                                        Phone
                                    </div>
                                    <div className="text-sm text-white group-hover:text-[#D4AF37] transition-colors">
                                        {CONTACT_PHONE}
                                    </div>
                                </div>
                            </a>
                        </div>

                            <div className="hidden lg:block relative mt-12 h-64 overflow-hidden border border-white/[0.05]">
                                <img
                                    src={BUSINESS_CONTEXT_URL}
                                    alt="Building websites for small businesses"
                                    className="w-full h-full object-cover opacity-60"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                                        Your Business Beautifully Built
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <div className="relative border border-white/[0.06] bg-[#050505] p-8 md:p-12">
                            <div className="absolute top-0 left-0 w-12 h-px bg-[#D4AF37]" />
                            <div className="absolute top-0 left-0 h-12 w-px bg-[#D4AF37]" />
                            <div className="absolute bottom-0 right-0 w-12 h-px bg-[#D4AF37]" />
                            <div className="absolute bottom-0 right-0 h-12 w-px bg-[#D4AF37]" />

                            {submitted ? (
                                <div
                                    data-testid="contact-success"
                                    className="py-16 text-center"
                                >
                                    <CheckCircle2
                                        className="w-12 h-12 text-[#D4AF37] mx-auto mb-6"
                                        strokeWidth={1.25}
                                    />
                                    <h3 className="font-display text-3xl text-white mb-4">
                                        Message received.
                                    </h3>
                                    <p className="text-sm text-[#C4C4C4] max-w-md mx-auto leading-relaxed">
                                        We&apos;ll be in touch shortly. If you&apos;d like
                                        to lock in a time now, book a slot on our
                                        calendar below.
                                    </p>
                                    {calendlyUrl ? (
                                        <button
                                            data-testid="success-book-time-btn"
                                            onClick={() => openCalendly(calendlyUrl)}
                                            className="mt-8 inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158]"
                                        >
                                            <Calendar className="w-4 h-4" strokeWidth={1.75} />
                                            Book a Time
                                        </button>
                                    ) : null}
                                    <button
                                        data-testid="contact-submit-another"
                                        onClick={() => setSubmitted(false)}
                                        className="mt-8 block mx-auto text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#E5C158] transition-colors border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1"
                                    >
                                        Submit Another
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                                    {/* Honeypot — hidden from real users, visible to bots */}
                                    <div
                                        aria-hidden="true"
                                        style={{
                                            position: "absolute",
                                            left: "-10000px",
                                            top: "auto",
                                            width: 1,
                                            height: 1,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <label htmlFor="website">
                                            Website (leave blank)
                                        </label>
                                        <input
                                            id="website"
                                            name="website"
                                            type="text"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            value={form.website}
                                            onChange={onChange("website")}
                                        />
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className={labelClass} htmlFor="name">
                                                Name
                                            </label>
                                            <input
                                                id="name"
                                                data-testid="form-name"
                                                type="text"
                                                value={form.name}
                                                onChange={onChange("name")}
                                                className={inputClass}
                                                placeholder="Jane Doe"
                                                autoComplete="name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass} htmlFor="email">
                                                Email
                                            </label>
                                            <input
                                                id="email"
                                                data-testid="form-email"
                                                type="email"
                                                value={form.email}
                                                onChange={onChange("email")}
                                                className={inputClass}
                                                placeholder="you@business.com"
                                                autoComplete="email"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label
                                                className={labelClass}
                                                htmlFor="business_name"
                                            >
                                                Business Name
                                            </label>
                                            <input
                                                id="business_name"
                                                data-testid="form-business-name"
                                                type="text"
                                                value={form.business_name}
                                                onChange={onChange("business_name")}
                                                className={inputClass}
                                                placeholder="Your company"
                                                autoComplete="organization"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass} htmlFor="phone">
                                                Phone
                                            </label>
                                            <input
                                                id="phone"
                                                data-testid="form-phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={onChange("phone")}
                                                className={inputClass}
                                                placeholder="(555) 555-5555"
                                                autoComplete="tel"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6">
                                        <label className={labelClass} htmlFor="message">
                                            How can we help?{" "}
                                            <span className="text-[#666] normal-case tracking-normal">
                                                (optional)
                                            </span>
                                        </label>
                                        <textarea
                                            id="message"
                                            data-testid="form-message"
                                            value={form.message}
                                            onChange={onChange("message")}
                                            rows={5}
                                            className={`${inputClass} resize-none`}
                                            placeholder="Tell us about your business, what kind of website you have in mind, timeline, or anything else that would help us prepare for your consultation…"
                                        />
                                    </div>

                                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                        <p className="text-xs text-[#888] font-light max-w-sm">
                                            By submitting, you agree to be contacted by
                                            RecoveryDesk regarding your inquiry.
                                        </p>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="form-submit"
                                            className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Submitting…
                                                </>
                                            ) : (
                                                "Send Message"
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
